import type { IUserDetailsEntity } from '@intigral/domain';
import { appConfig } from '../config/app.config';
import { GUEST_SUBSCRIPTION_DEFAULTS } from './subscriptionDefaults';
import type { GuestPublicConfig, PublicConfigBody } from './types';

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length > 0;
}

function pickSubscriptionList(data: Record<string, unknown>): IUserDetailsEntity['subscriptionStatus'] | null {
    if (Array.isArray(data.subscriptionStatus) && data.subscriptionStatus.length > 0) {
        return data.subscriptionStatus as IUserDetailsEntity['subscriptionStatus'];
    }
    if (Array.isArray(data.subList) && data.subList.length > 0) {
        return data.subList as IUserDetailsEntity['subscriptionStatus'];
    }
    return null;
}

function deriveSubscriptionGuids(list: NonNullable<IUserDetailsEntity['subscriptionStatus']>): string[] {
    return list
        .filter((item) => item?.subscriptionGuid && (item.active === undefined || item.active === true))
        .map((item) => item.subscriptionGuid);
}

export function parseGuestPublicConfig(raw: PublicConfigBody, client: string): GuestPublicConfig | null {
    const publicConfig = raw.data ?? raw;
    const anonymousUser = publicConfig?.anonymous_user;
    const envKey = publicConfig?.envKey;
    if (!anonymousUser || !envKey) {
        return null;
    }
    return {
        anonymousUser,
        envKey,
        client: publicConfig?.client || client,
    };
}

export function normalizeGuestLoginResponse(
    response: unknown,
    options?: { anonymousUsername?: string; envKey?: string }
): IUserDetailsEntity | null {
    const root = response as Record<string, unknown> | null;
    if (!root || typeof root !== 'object') {
        return null;
    }

    const inner = root.data;
    const data =
        inner && typeof inner === 'object' && 'data' in (inner as object)
            ? ((inner as { data: unknown }).data as Record<string, unknown>)
            : (inner as Record<string, unknown>) ?? root;

    if (!data || typeof data !== 'object') {
        return null;
    }

    const username =
        (typeof data.username === 'string' && data.username) ||
        (typeof data.loginUsername === 'string' && data.loginUsername) ||
        options?.anonymousUsername;

    if (!username) {
        return null;
    }

    const authToken = 'guest';

    const firstLoginDate =
        typeof data.firstLoginDate === 'number'
            ? data.firstLoginDate
            : typeof data.createdTime === 'number'
            ? data.createdTime
            : Date.now();

    const guid =
        (typeof data.guid === 'string' && data.guid) ||
        (typeof data.userClusterKey === 'string' && data.userClusterKey) ||
        `guest-${data.accountId ?? username}`;

    const fromApiSubs = pickSubscriptionList(data);
    const defaultSubs = GUEST_SUBSCRIPTION_DEFAULTS.subscriptionStatus ?? [];
    const subscriptionList =
        fromApiSubs && fromApiSubs.length > 0
            ? fromApiSubs.map((item, index) => ({
                  ...(defaultSubs[index] ?? defaultSubs[0]),
                  ...item,
              }))
            : defaultSubs;

    const tierStatus = (
        isPlainObject(data.tierStatus)
            ? { ...GUEST_SUBSCRIPTION_DEFAULTS.tierStatus, ...data.tierStatus }
            : GUEST_SUBSCRIPTION_DEFAULTS.tierStatus
    ) as IUserDetailsEntity['tierStatus'];

    const tierSource = data.tierConfig ?? data.tier;
    const tierConfig = (
        isPlainObject(tierSource)
            ? { ...GUEST_SUBSCRIPTION_DEFAULTS.tierConfig, ...tierSource }
            : GUEST_SUBSCRIPTION_DEFAULTS.tierConfig
    ) as IUserDetailsEntity['tierConfig'];

    const subscriptionRev =
        typeof data.subscriptionRev === 'number'
            ? data.subscriptionRev
            : typeof data.subRev === 'number'
            ? data.subRev
            : GUEST_SUBSCRIPTION_DEFAULTS.subscriptionRev ?? 0;

    const identityFromApi =
        typeof data.identityToken === 'string' && data.identityToken.trim().length > 0 ? data.identityToken.trim() : '';
    const authSignFromApi =
        typeof data.authSign === 'string' && data.authSign.trim().length > 0 ? data.authSign.trim() : '';

    const subscriptionGUID =
        Array.isArray(data.subscriptionGUID) && data.subscriptionGUID.length > 0
            ? (data.subscriptionGUID as string[])
            : deriveSubscriptionGuids(subscriptionList);

    return {
        ...data,
        isGuest: true,
        username,
        authToken,
        guid,
        userExists: data.userExists !== false,
        firstLoginDate,
        authSign: authSignFromApi || 'guest',
        identityToken: identityFromApi || 'guest',
        productKey:
            (typeof data.productKey === 'string' && data.productKey) ||
            (typeof data.productkey === 'string' && data.productkey) ||
            appConfig.productKey ||
            '',
        operator: typeof data.operator === 'string' ? data.operator : 'DEFAULT',
        accountId: typeof data.accountId === 'number' ? data.accountId : Number(data.accountId) || 0,
        userStatus: typeof data.userStatus === 'string' ? data.userStatus : 'ACTIVE',
        apiKey:
            (typeof data.apiKey === 'string' && data.apiKey) ||
            options?.envKey ||
            process.env.NX_PUBLIC_WCP_STATIC_API_KEY ||
            undefined,
        userClusterKey: typeof data.userClusterKey === 'string' ? data.userClusterKey : undefined,
        netflixSubStatus: data.netflixSubStatus ?? null,
        tvodList: Array.isArray(data.tvodList) ? data.tvodList : [],
        subscriptionStatus: subscriptionList,
        tierStatus,
        tierConfig,
        subscriptionRev,
        subscriptionGUID,
    } as IUserDetailsEntity;
}
